import * as React from 'react'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'

import OverflowToolbarPanel, {
    HammockToolbarButtonData,
    HammockToolbarProps,
    useOverflowToolbarPanelStyles,
} from '../../../components/Overflow/OverflowToolbarPanel'
import { generatePath } from 'react-router'
import { fileRoutePrefix } from '../../../hooks/file/useFilePages'
import { ClipboardTaskIcon, FlowchartIcon, MailIcon, PeopleIcon, TimerIcon } from '../../../components/MainMenu'
import {
    BriefcaseMedicalFilled,
    BriefcaseMedicalRegular,
    bundleIcon,
    ChatHelpFilled,
    ChatHelpRegular,
    DocumentLandscapeDataFilled,
    DocumentLandscapeDataRegular,
    DocumentLinkFilled,
    DocumentLinkRegular,
    DocumentLockFilled,
    DocumentLockRegular,
    DocumentMultipleFilled,
    DocumentMultipleRegular,
    GiftCardFilled,
    GiftCardRegular,
    HeartBrokenFilled,
    HeartBrokenRegular,
    NotebookFilled,
    NotebookRegular,
    NoteFilled,
    NoteRegular,
    PaymentFilled,
    PaymentRegular,
    TargetEditFilled,
    TargetEditRegular,
    VehicleCarProfileFilled,
    VehicleCarProfileRegular,
} from '@fluentui/react-icons'
import { ModuleType } from '../../../context/GlobalContext'
import useRoles, { Role, rolesTasks, rolesTimes } from '../../../hooks/useRoles'
import useModules from '../../../hooks/module/useModules'
import { usePageStyles, ViewFileProps } from './ViewFile'
import { useFileModules } from '../../../hooks/module/useFileModule'
import { Card, mergeClasses } from '@fluentui/react-components'

const DisbursementIcon = bundleIcon(PaymentFilled, PaymentRegular)
const DocumentIcon = bundleIcon(DocumentMultipleFilled, DocumentMultipleRegular)
const DocumentSharedIcon = bundleIcon(DocumentLinkFilled, DocumentLinkRegular)
const FixedPriceIcon = bundleIcon(GiftCardFilled, GiftCardRegular)
const LegalAidCertificateIcon = bundleIcon(ChatHelpFilled, ChatHelpRegular)
const DocketIcon = bundleIcon(NotebookFilled, NotebookRegular)
const InjuryIcon = bundleIcon(BriefcaseMedicalFilled, BriefcaseMedicalRegular)
const TravelIcon = bundleIcon(VehicleCarProfileFilled, VehicleCarProfileRegular)
const NoteIcon = bundleIcon(NoteFilled, NoteRegular)
const PersonLawIcon = bundleIcon(HeartBrokenFilled, HeartBrokenRegular)
const RegistrationIcon = bundleIcon(TargetEditFilled, TargetEditRegular)
const MonitoringIcon = bundleIcon(DocumentLandscapeDataFilled, DocumentLandscapeDataRegular)
const AuditTrailIcon = bundleIcon(DocumentLockFilled, DocumentLockRegular)

export enum ToolbarItemKey {
    Party = 'party',
    Email = 'email',
    Document = 'document',
    SharedDocument = 'shareddocument',
    Task = 'task',
    Workflow = 'workflow',
    Time = 'time',
    Disbursement = 'disbursement',
    FixedPrice = 'fixed-price',
    LegalAidCertificate = 'legal-aid-certificate',
    Docket = 'docket',
    Injury = 'injury',
    Travel = 'travel',
    Note = 'note',
    FamilyLaw = 'family-law',
    Monitoring = 'monitoring',
    AuditTrail = 'audit-trail',
}

export default function ViewFileTabs(props: ViewFileProps): ReactElement {
    const history = useHistory()
    const modules = useModules()
    const roles = useRoles()
    const pageStyles = usePageStyles()
    const toolbarStyles = useOverflowToolbarPanelStyles()

    const fileModules = useFileModules(props.file)

    const toolbarProps = useMemo<HammockToolbarProps>(
        (): HammockToolbarProps => ({
            className: pageStyles.toolbar,
        }),
        [],
    )

    const [activeButton, setActiveButton] = useState<string>(props.routeMatch.params?.tab || ToolbarItemKey.Document)
    const onSelectButton = (value?: string) => {
        const button: HammockToolbarButtonData = buttons
            .flatMap(button => [button, ...(button.childButtons || [])])
            .find(button => (button.value as string) === value)
        if (button?.route) {
            props.setSelectedDriveItemIds([])
            props.setSelectedDriveItems([])
            history.push(generatePath(fileRoutePrefix + button?.route, props.routeMatch.params))
        }
        setActiveButton(value)
    }

    const buttons = useMemo<HammockToolbarButtonData[]>(
        (): HammockToolbarButtonData[] =>
            [
                {
                    value: ToolbarItemKey.Party,
                    icon: <PeopleIcon />,
                    titleKey: 'file.pivot.party.title',
                    route: '/party',
                    onClick: onSelectButton,
                },
                {
                    value: ToolbarItemKey.Email,
                    icon: <MailIcon />,
                    titleKey: 'file.pivot.email.title',
                    route: '/email',
                    onClick: onSelectButton,
                },
                {
                    value: ToolbarItemKey.Document,
                    icon: <DocumentIcon />,
                    titleKey: 'file.pivot.document.title',
                    route: '/document',
                    onClick: onSelectButton,
                },
                {
                    value: ToolbarItemKey.SharedDocument,
                    icon: <DocumentSharedIcon />,
                    titleKey: 'file.pivot.shareddocument.title',
                    route: '/document',
                    filter: () => false,
                    onClick: onSelectButton,
                },
                {
                    value: ToolbarItemKey.Task,
                    icon: <ClipboardTaskIcon />,
                    titleKey: 'file.pivot.task.title',
                    route: '/task',
                    filter: () => modules.any([ModuleType.Tasks]) && roles.any(rolesTasks),
                    onClick: onSelectButton,
                },
                {
                    value: ToolbarItemKey.Workflow,
                    icon: <FlowchartIcon />,
                    titleKey: 'file.pivot.workflow.title',
                    route: '/workflow',
                    filter: () => modules.any([ModuleType.Workflow]) && roles.any(rolesTasks),
                    onClick: onSelectButton,
                },
                {
                    value: 'registration',
                    icon: <RegistrationIcon />,
                    titleKey: 'file.pivot.registration.title',
                    childButtons: [
                        {
                            value: ToolbarItemKey.Time,
                            icon: <TimerIcon />,
                            titleKey: 'file.pivot.time.title',
                            route: '/time',
                            filter: () => modules.has(ModuleType.TimeTracking) && roles.any(rolesTimes),
                            onClick: onSelectButton,
                        },
                        {
                            value: ToolbarItemKey.Travel,
                            icon: <TravelIcon />,
                            titleKey: 'file.pivot.travel.title',
                            route: '/travel',
                            filter: () => modules.has(ModuleType.TravelExpenses),
                            onClick: onSelectButton,
                        },
                        {
                            value: ToolbarItemKey.Disbursement,
                            icon: <DisbursementIcon />,
                            titleKey: 'file.pivot.disbursement.title',
                            route: '/disbursement',
                            filter: () => modules.has(ModuleType.Disbursements),
                            onClick: onSelectButton,
                        },
                        {
                            value: ToolbarItemKey.FixedPrice,
                            icon: <FixedPriceIcon />,
                            titleKey: 'file.pivot.fixedprice.title',
                            route: '/fixed-price',
                            filter: () => modules.has(ModuleType.FixedPrices),
                            onClick: onSelectButton,
                        },
                        {
                            value: ToolbarItemKey.Injury,
                            icon: <InjuryIcon />,
                            titleKey: 'file.pivot.injury.title',
                            route: '/injury',
                            filter: () => fileModules.has(ModuleType.Injury),
                            onClick: onSelectButton,
                        },
                        {
                            value: ToolbarItemKey.FamilyLaw,
                            icon: <PersonLawIcon />,
                            titleKey: 'file.pivot.familylaw.title',
                            route: '/family-law',
                            filter: () => fileModules.has(ModuleType.FamilyLaw),
                            onClick: onSelectButton,
                        },
                        {
                            value: ToolbarItemKey.LegalAidCertificate,
                            icon: <LegalAidCertificateIcon />,
                            titleKey: 'file.pivot.legalaidcertificate.title',
                            route: '/legal-aid-certificate',
                            filter: () => fileModules.has(ModuleType.LegalAid),
                            onClick: onSelectButton,
                        },
                        {
                            value: ToolbarItemKey.Docket,
                            icon: <DocketIcon />,
                            titleKey: 'file.pivot.docketproceeding.title',
                            route: '/docket',
                            filter: () => fileModules.has(ModuleType.Docket),
                            onClick: onSelectButton,
                        },
                    ].filter((item: HammockToolbarButtonData) => (item.filter ? item.filter() : true)),
                },
                {
                    value: ToolbarItemKey.Note,
                    icon: <NoteIcon />,
                    titleKey: 'file.pivot.note.title',
                    route: '/note',
                    onClick: onSelectButton,
                },
                {
                    value: ToolbarItemKey.Monitoring,
                    icon: <MonitoringIcon />,
                    titleKey: 'file.pivot.monitoring.title',
                    route: '/monitoring',
                    filter: () => modules.has(ModuleType.Invoices) && roles.any([Role.Invoice]),
                    onClick: onSelectButton,
                },
                {
                    value: ToolbarItemKey.AuditTrail,
                    icon: <AuditTrailIcon />,
                    titleKey: 'file.pivot.audittrail.title',
                    route: '/audit-trail',
                    filter: () => modules.has(ModuleType.AuditTrail),
                    onClick: onSelectButton,
                },
            ].filter((item: HammockToolbarButtonData) => (item.filter ? item.filter() : true)),
        [],
    )

    const activeParentButton = useMemo(
        () =>
            buttons
                .flatMap(button => (button.childButtons || []).map(child => ({ parent: button, value: child })))
                .filter(button => button.value.value === activeButton)
                .map(button => button.parent.value)?.[0],
        [activeButton],
    )

    return (
        <Card className={mergeClasses(pageStyles.card, pageStyles.toolbarColor)}>
            <OverflowToolbarPanel
                containerClassName={mergeClasses(pageStyles.toolbarTabs, pageStyles.toolbarColor)}
                toolbarProps={toolbarProps}
                getToolbarButtonProps={button => ({
                    appearance: activeParentButton === button?.value || activeButton === button?.value ? 'primary' : 'subtle',
                    disabled: button?.disabled,
                })}
                getMenuItemProps={button => ({
                    className: activeButton === button?.value && toolbarStyles.selectedMenuItem,
                    disabled: button?.disabled,
                })}
                buttons={buttons}
                overflowButtonLabelKey={'base.item.label'}
            />
        </Card>
    )
}
